// ts-rest contract for Comply > TC Tracker.
// Composes the TcRecord + CpdEntry Object Type rows behind one typed read.
// Per ADR-0018 the rows the server knows the shape of are real `z.object`
// schemas (the precise InferObject projection), not `z.record(z.unknown())`.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// The TcRecord Object Type row (InferObject), projected field-by-field. The
// cumulative CPD fields (cpdHoursCompleted / ragStatus / daysUntilAssessment)
// are deliberately NOT on the row — they are read-derived from CpdEntry rows
// and `nextAssessmentAt` vs now (see tc-record.ts). Staff identity is the
// actor id; there is no Employee Object Type in v1, so no `personName`.
export const tcRecordDtoSchema = z.object({
    id: z.string(),
    staffActorId: z.string(),
    role: z.string(),
    supervisorActorId: z.string().nullable(),
    competencyStatus: z.enum(['Competent', 'AttainingCompetence', 'Lapsed', 'SupervisedOnly']),
    lastAssessmentAt: z.string().nullable(),
    nextAssessmentAt: z.string(),
    cpdYear: z.string(),
    cpdHoursRequired: z.number(),
    areas: z.array(z.string()),
});
export type TcRecordDto = z.infer<typeof tcRecordDtoSchema>;

// The CpdEntry Object Type row (InferObject), projected field-by-field.
export const cpdEntryDtoSchema = z.object({
    id: z.string(),
    staffActorId: z.string(),
    activity: z.string(),
    provider: z.string(),
    date: z.string(),
    hours: z.number(),
    category: z.enum(['Technical', 'Regulatory', 'Ethics', 'ProductKnowledge', 'SoftSkills']),
    evidenceHeld: z.boolean(),
    recordedAt: z.string(),
    recordedById: z.string(),
});
export type CpdEntryDto = z.infer<typeof cpdEntryDtoSchema>;

export const tcTrackerResponseSchema = z.object({
    tcRecords: z.array(tcRecordDtoSchema),
    cpdEntries: z.array(cpdEntryDtoSchema),
});
export type TcTrackerResponse = z.infer<typeof tcTrackerResponseSchema>;

import { errorBody } from '../schemas/common';
const okBody = z.object({ ok: z.literal(true) });

// Mirrors the RecordCpdEntry Action input (ADR-0008 write-side rule): the body
// supplies exactly what the Action requires. `staffActorId` is the beneficiary
// staff member; `recordedById` is set server-side from the acting actor.
export const recordCpdEntryBodySchema = z.object({
    staffActorId: z.string().uuid(),
    activity: z.string().min(1),
    provider: z.string(),
    date: z.string(),
    hours: z.number(),
    category: z.enum(['Technical', 'Regulatory', 'Ethics', 'ProductKnowledge', 'SoftSkills']),
    evidenceHeld: z.boolean().optional(),
});
export type RecordCpdEntryBody = z.infer<typeof recordCpdEntryBodySchema>;

export const complyTcTrackerContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/tc-tracker',
            responses: { 200: tcTrackerResponseSchema, 403: errorBody },
            summary: 'Composed Comply > TC Tracker payload',
        },
        recordCpdEntry: {
            method: 'POST',
            path: '/comply/tc/cpd',
            body: recordCpdEntryBodySchema,
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Record a CPD entry',
        },
        completeAttestation: {
            method: 'POST',
            path: '/comply/tc/:recordId/attest',
            pathParams: z.object({ recordId: z.string() }),
            body: z.object({
                attestationType: z.enum(['smcr', 'consumer_duty', 'aml']),
            }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Complete a TC attestation on a TC record',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
